import React, { useState, useEffect } from "react";
import { User, Briefcase, Palette, Heart } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ReactComponent as HostIcon } from "../../../assets/client.svg";
import { ReactComponent as ProfessionalIcon } from "../../../assets/Pro.svg";
import { useGoogleLogin } from "@react-oauth/google";
import FacebookLogin from "@greatsumini/react-facebook-login";
import { FACEBOOK_APP_ID } from "src/config/facebookAuth";
import { useToast } from "../../../context/ToastContext";

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

export default function CreateAccount({
  onNext,
  onGoToLogin,
  initialData = {},
}) {
  const { showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(true); // ✅ DEFAULT TO TRUE

  const [data, setData] = useState({
    fullName: initialData.fullName || "",
    email: initialData.email || "",
    password: initialData.password || "",
    countryCode: initialData.countryCode || "+91",
    phone: (() => {
      const raw = initialData.phone || "";
      const cc = initialData.countryCode || "+91";
      return raw.startsWith(cc)
        ? raw.slice(cc.length)
        : raw.replace(/^\+\d+/, "");
    })(),
    confirmPassword: initialData.confirmPassword || "",
    role: initialData.role || "", // "customer" | "professional"
  });

  const [errors, setErrors] = useState({});
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      console.log(tokenResponse);
      const payload = {
        isGoogleAuth: true,
        googleToken: tokenResponse.access_token,
        role: data.role,
      };
      setLoading(true);
      try {
        onNext(payload); // passes the google logic back to registration Modal.
      } catch (err) {
        setErrors({ general: err.message || "Google authentication failed." });
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google login error:", error);
      setErrors({ general: "Google authentication failed." });
    },
  });

  const handleFacebookResponse = async (response) => {
    if (response.status === "unknown") return;

    try {
      const payload = {
        isFacebookAuth: true,
        facebookToken: response.accessToken,
        role: data.role,
      };

      onNext(payload);
    } catch (error) {
      console.error("Facebook Login failed", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (value) => {
    setData((prev) => ({ ...prev, role: value }));
    setErrors((prev) => ({ ...prev, role: "" }));
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!data.fullName.trim()) newErrors.fullName = "Full Name is required.";
    if (!data.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!emailRegex.test(data.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    const fullPhone = data.phone ? `${data.countryCode}${data.phone}` : "";

    if (!data.phone) {
      newErrors.phone = "Phone number is required.";
    } else if (fullPhone) {
      const phoneRegex = /^\+\d{10,15}$/;
      if (!phoneRegex.test(fullPhone)) {
        newErrors.phone = "Please enter a valid phone number.";
      }
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

    if (!data.password) {
      newErrors.password = "Password is required.";
    } else if (!passwordRegex.test(data.password)) {
      newErrors.password =
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.";
    }
    if (!data.confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required.";
    } else if (data.password !== data.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }
    if (!agreeTerms) {
      newErrors.terms = "You must accept the terms and conditions.";
    }
    if (!data.role) {
      newErrors.role = "Please select a role to continue.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstErrMsg = Object.values(newErrors)[0];
      if (firstErrMsg) showError(firstErrMsg);
      return;
    }
    const payload = {
      ...data,
      phone: data.phone ? `${data.countryCode}${data.phone}` : "",
    };
    setLoading(true);
    try {
      await onNext(payload);
      console.log(payload);
    } catch (err) {
      setErrors({ general: err.message || "Registration failed." });
      showError(err.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    {
      value: "customer",
      label: "Client",
      icon: User,
      description:
        "Find trusted event professionals, compare quotes and plan easily.",
    },
    {
      value: "professional",
      label: "Event Pro",
      icon: Briefcase,
      description:
        "Get booked by clients, showcase your work, and manage bookings in dashboard.",
    },
  ];

  return (
    <div className="flex flex-col md:flex-row rounded-2xl w-full mx-auto md:min-h-[550px] min-h-0 p-1 sm:p-2 md:p-0 bg-white">
      {/* Role / Header Side (Left on Desktop, Top on Mobile) */}
      <div className="md:shrink-0 w-full md:w-1/2 flex flex-col items-start justify-start p-3 md:p-6 rounded-2xl bg-[#F7F3F2] border border-transparent md:border-[#e2dfdf] gap-2 md:gap-3 mb-4 md:mb-0">
        <div className="px-4 py-1.5 rounded-full text-xs md:text-sm font-medium bg-[#FFF0EA] border border-[#FF4C18] text-[#FF4C18] inline-flex">
          Create Account
        </div>
        <h1
          className="text-2xl md:text-3xl"
          style={{ fontFamily: "CabinetGrotesk-Extrabold" }}
        >
          Let's Get Started.
        </h1>

        <p
          className="text-[#010101] text-sm md:text-base py-0.5 w-full md:max-w-[85%]"
          style={{ fontFamily: "CabinetGrotesk-Regular" }}
        >
          Choose how you'd like to join GoPratle and get access to features
          tailored to your role.
        </p>

        {errors.role && (
          <div className="w-full mt-1 border border-[#FF4C18] bg-[#FFF0EA] rounded-xl p-3">
            <span className="text-[#FF4C18] font-medium text-sm md:text-base">Please select a role to continue.</span>{" "}
            <span className="text-[#010101] text-xs md:text-sm">Your role determines the features and workflow available in your account.</span>
          </div>
        )}

        <div className="w-full grid grid-cols-2 md:flex md:flex-col gap-2 md:gap-3 py-1 mt-1">
          {/* Role Selection - Grid Layout */}
          {roleOptions.map((option, index) => (
            <label
              key={option.value}
              className={`rounded-xl p-2.5 md:p-3.5
              flex flex-col md:flex-row items-start md:items-center justify-start text-left
              transition-all duration-150 border
              gap-2 md:gap-3
              ${loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer active:scale-[0.98] hover:shadow-md'}
              ${
                data.role === option.value
                  ? "border-[#FF4C18] bg-[#FFF0EA] text-[#FF4C18]"
                  : "border-gray-200 bg-white text-black shadow-sm"
              }
              `}
            >
              <input
                type="radio"
                name="role"
                value={option.value}
                checked={data.role === option.value}
                onChange={() => !loading && handleRoleChange(option.value)}
                disabled={loading}
                className="sr-only"
              />

              {option.value === "customer" && (
                <HostIcon
                  className={`h-8 w-8 aspect-square md:h-full md:w-12 flex-shrink-0 `}
                />
              )}
              {option.value === "professional" && (
                <ProfessionalIcon
                  className={`h-8 w-8 aspect-square md:h-full md:w-12 flex-shrink-0 `}
                />
              )}
              <div className="w-full flex flex-col items-start justify-start gap-1">
                <p
                  className="text-sm md:text-lg font-bold"
                  style={{ fontFamily: "CabinetGrotesk-Bold" }}
                >
                  {option.label}
                </p>
                <p
                  className={`text-[10px] md:text-sm leading-tight text-left ${data.role === option.value ? "text-[#FF4C18]" : "text-gray-500"}`}
                  style={{ fontFamily: "CabinetGrotesk-Regular" }}
                >
                  {option.description}
                </p>
              </div>
            </label>
          ))}
        </div>
        <p
          className="text-sm md:text-md text-[#6B6864] w-[70%]"
          style={{ fontFamily: "CabinetGrotesk-Regular" }}
        >
          By creating an account you agree to our &nbsp;
          <a
            href="terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#FF4C18] underline"
          >
            Terms & Conditions
          </a>
          &nbsp;and&nbsp;
          <a
            href="privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#FF4C18] underline"
          >
            Privacy Policy
          </a>
          .
        </p>
        <hr className="w-full border-gray-200 block md:hidden mt-2" />
      </div>

      {/* Form Side (Right on Desktop, Bottom on Mobile) */}
      <div className="md:shrink-0 w-full md:w-1/2 p-2 md:px-6 md:py-4 space-y-3 flex flex-col justify-start md:justify-center">
        {/* Input Fields */}
        <div className="space-y-4">
          <div className={`px-4 py-1.5 rounded-full inline-flex self-start items-center justify-center mb-1 transition-opacity duration-200 ${data.role ? "bg-[#FFF0EA] opacity-100" : "opacity-0 pointer-events-none select-none"}`}>
            <p className="text-xs md:text-sm text-[#FF4C18] font-medium leading-normal">
              Signing up as{" "}
              <span className="capitalize ml-1">
                {data.role === "customer" ? "Event Host" : "Professional"}
              </span>
            </p>
          </div>
          {[
            { id: "fullName", type: "text", label: "Full Name" },
            { id: "email", type: "email", label: "Email Address" },
          ].map(({ id, type, label }) => (
            <div key={id} className="relative w-full pt-1">
              <input
                id={id}
                type={type}
                name={id}
                value={data[id]}
                onChange={handleChange}
                placeholder=" "
                disabled={loading}
                className={`peer w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF4C18] ${loading ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}`}
              />
              <label
                htmlFor={id}
                className="absolute left-3 top-[-5px] bg-white px-1 text-[11px] md:text-xs text-[#FF4C18] transition-all
                peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-sm
                peer-focus:top-[-5px] peer-focus:translate-y-0 peer-focus:text-[11px] md:peer-focus:text-xs peer-focus:text-[#FF4C18]"
              >
                {label}
              </label>
              {errors[id] && (
                <p className="text-red-500 text-xs mt-1">{errors[id]}</p>
              )}
            </div>
          ))}

          <div className="relative w-full pt-1">
            <label className="absolute left-3 top-[-5px] bg-white px-1 text-[11px] md:text-xs text-[#FF4C18] z-10">
              Phone Number
            </label>

            <div className="flex gap-2 w-full">
              {/* Country Code */}
              <select
                className={`w-[80px] md:w-24 shrink-0 rounded-lg border border-gray-300 bg-white px-1 md:px-2 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF4C18] ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
                value={data.countryCode}
                disabled={loading}
                onChange={(e) =>
                  setData((prev) => ({ ...prev, countryCode: e.target.value }))
                }
              >
                <option value="+91">+91</option>
                <option value="+1">+1</option>
                <option value="+44">+44</option>
                <option value="+61">+61</option>
                <option value="+81">+81</option>
                <option value="+86">+86</option>
              </select>

              {/* Phone Number */}
              <input
                type="tel"
                value={data.phone}
                disabled={loading}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, "");
                  setData((prev) => ({ ...prev, phone: onlyDigits }));
                  setErrors((prev) => ({ ...prev, phone: "" }));
                }}
                placeholder="Enter phone number"
                className={`flex-1 min-w-0 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF4C18] ${loading ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}`}
              />
            </div>

            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>

          {["password", "confirmPassword"].map((key) => (
            <div key={key} className="relative w-full mt-4 pt-1">
              <input
                id={key}
                type={showPassword ? "text" : "password"}
                name={key}
                value={data[key]}
                onChange={handleChange}
                placeholder=" "
                disabled={loading}
                className={`peer w-full rounded-lg border border-gray-300 bg-white px-3 py-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF4C18] ${loading ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}`}
              />
              <label
                htmlFor={key}
                className="absolute left-3 top-[-5px] bg-white px-1 text-[11px] md:text-xs text-[#FF4C18] transition-all
                peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-sm
                peer-focus:top-[-5px] peer-focus:text-[11px] md:peer-focus:text-xs peer-focus:text-[#FF4C18]"
              >
                {key === "confirmPassword" ? "Confirm Password" : "Password"}
              </label>
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-3 text-gray-500 focus:outline-none flex items-center justify-center h-6 w-6"
              >
                {showPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
              {errors[key] && (
                <p className="text-red-500 text-xs mt-1">{errors[key]}</p>
              )}
            </div>
          ))}

          <button
            onClick={handleSubmit}
            className={`w-full bg-[#FF4C18] text-white py-2 rounded-xl hover:scale-105 transition font-semibold flex items-center justify-center ${
              loading ? "opacity-60 cursor-not-allowed" : ""
            }`}
            disabled={loading}
          >
            {loading && Object.keys(errors).length === 0 ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Creating...
              </>
            ) : (
              "Create Account"
            )}
          </button>
        </div>

        <p className="text-sm text-center mt-4 text-gray-500">
          Already have an account?{" "}
          <button
            onClick={onGoToLogin}
            className="text-[#FF4C18] hover:underline"
          >
            Login
          </button>
        </p>

        {/* or */}
        <div className="w-full flex items-center justify-center py-2 mt-4">
          <div className="flex-grow h-px bg-gray-300"></div>
          <span className="px-4 text-md text-gray-500 font-medium">Or</span>
          <div className="flex-grow h-px bg-gray-300"></div>
        </div>

        <div className="flex flex-col gap-3 w-full">
          <button
            className={`w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-2 text-sm font-medium text-gray-700 transition ${loading ? 'opacity-60 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-50'}`}
            disabled={loading}
            onClick={() => {
              if (!data.role) {
                setErrors((prev) => ({ ...prev, role: "Please select a role to continue." }));
                return;
              }
              handleGoogleLogin();
            }}
          >
            <GoogleIcon /> Continue with Google
          </button>
          <FacebookLogin
            appId={FACEBOOK_APP_ID}
            onSuccess={(response) => handleFacebookResponse(response)}
            onFail={(error) => console.log("Login Failed!", error)}
            onProfileSuccess={(response) =>
              console.log("Profile fetch successful!", response)
            }
            render={({ onClick }) => (
              <button
                onClick={(e) => {
                  if (!data.role) {
                    setErrors((prev) => ({ ...prev, role: "Please select a role to continue." }));
                    e.preventDefault();
                    return;
                  }
                  if (!loading) onClick(e);
                }}
                disabled={loading}
                className={`w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-2 text-sm font-medium text-gray-700 transition ${loading ? 'opacity-60 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-50'}`}
              >
                <FacebookIcon /> Continue with Facebook
              </button>
            )}
          />
        </div>
      </div>
    </div>
  );
}
